import api from './api';

export interface EmailTemplate {
  id: number;
  title: string;
  module: string;
  subjectTemplate: string;
  bodyTemplate: string;
  organizationId: number;
  branchId: number | null;
  isDefault: boolean;
  emailSendBehavior?: 'auto' | 'ask' | 'never';
  createdBy: number;
  createdAt: string;
  updatedAt: string;
}

export interface EmailTemplateFormData {
  title: string;
  subjectTemplate: string;
  bodyTemplate: string;
  isDefault?: boolean;
}

export interface EmailTemplateResponse {
  success: boolean;
  data: EmailTemplate;
  message?: string;
}

export interface EmailTemplatesResponse {
  success: boolean;
  data: EmailTemplate[];
  message?: string;
}

export const emailTemplateApi = {
  // Get all templates for documents module
  getDocumentsTemplates: async (): Promise<EmailTemplatesResponse> => {
    const response = await api.get('/email-templates/documents');
    return response.data;
  },

  // Get all templates for rejected documents module
  getRejectedDocumentsTemplates: async (): Promise<EmailTemplatesResponse> => {
    const response = await api.get('/email-templates/rejected-documents');
    return response.data;
  },

  // Get all templates for leads module
  getLeadsTemplates: async (): Promise<EmailTemplatesResponse> => {
    const response = await api.get('/email-templates/leads');
    return response.data;
  },

  // Create a new template for documents
  createTemplate: async (templateData: EmailTemplateFormData, module: string = 'documents'): Promise<EmailTemplateResponse> => {
    const endpoint = module === 'rejected-documents' ? '/email-templates/rejected-documents' : '/email-templates/documents';
    const response = await api.post(endpoint, templateData);
    return response.data;
  },

  // Create a new template for rejected documents
  createRejectedDocumentsTemplate: async (templateData: EmailTemplateFormData): Promise<EmailTemplateResponse> => {
    const response = await api.post('/email-templates/rejected-documents', templateData);
    return response.data;
  },

  // Create a new template for leads
  createLeadsTemplate: async (templateData: EmailTemplateFormData): Promise<EmailTemplateResponse> => {
    const response = await api.post('/email-templates/leads', templateData);
    return response.data;
  },

  // Update a template
  updateTemplate: async (id: number, templateData: Partial<EmailTemplateFormData>): Promise<EmailTemplateResponse> => {
    const response = await api.put(`/email-templates/${id}`, templateData);
    return response.data;
  },

  // Delete a template
  deleteTemplate: async (id: number): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete(`/email-templates/${id}`);
    return response.data;
  },

  // Set a template as default
  setDefaultTemplate: async (id: number): Promise<EmailTemplateResponse> => {
    const response = await api.post(`/email-templates/${id}/set-default`);
    return response.data;
  },

  // Fill template with data (for documents)
  fillTemplate: (template: EmailTemplate, data: {
    leadName?: string;
    leadEmail?: string;
    documentType?: string;
    documentUploadLink?: string;
  }): { subject: string; body: string } => {
    let subject = template.subjectTemplate;
    let body = template.bodyTemplate;

    const replacements: Record<string, string> = {
      '{{lead_name}}': data.leadName || '',
      '{{lead_email}}': data.leadEmail || '',
      '{{document_type}}': data.documentType || '',
      '{{document_upload_link}}': data.documentUploadLink || '',
    };

    // Replace all occurrences of each tag
    Object.entries(replacements).forEach(([tag, value]) => {
      const regex = new RegExp(tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      subject = subject.replace(regex, value);
      body = body.replace(regex, value);
    });

    return { subject, body };
  },

  // Fill lead template with lead data
  fillLeadTemplate: (template: EmailTemplate, leadData: {
    fullName?: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    leadUid?: string;
    createdAt?: string;
    company?: string;
    phone?: string;
    mobileNumber?: string;
    alternativeMobileNumber?: string;
    jobTitle?: string;
  }): { subject: string; body: string } => {
    let subject = template.subjectTemplate;
    let body = template.bodyTemplate;

    // Format dates
    const formatDate = (date?: string | Date): string => {
      if (!date) return '';
      const d = date instanceof Date ? date : new Date(date);
      return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const currentDate = formatDate(new Date());
    const leadDate = leadData.createdAt ? formatDate(leadData.createdAt) : '';

    const replacements: Record<string, string> = {
      '{{lead_name}}': leadData.fullName || `${leadData.firstName || ''} ${leadData.lastName || ''}`.trim() || '',
      '{{lead_first_name}}': leadData.firstName || '',
      '{{lead_last_name}}': leadData.lastName || '',
      '{{lead_email}}': leadData.email || '',
      '{{lead_id}}': leadData.leadUid || '',
      '{{lead_date}}': leadDate,
      '{{lead_company}}': leadData.company || '',
      '{{lead_phone}}': leadData.phone || '',
      '{{lead_mobile}}': leadData.mobileNumber || leadData.alternativeMobileNumber || '',
      '{{lead_job_title}}': leadData.jobTitle || '',
      '{{current_date}}': currentDate
    };

    // Replace all occurrences of each tag
    Object.entries(replacements).forEach(([tag, value]) => {
      const regex = new RegExp(tag.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      subject = subject.replace(regex, value);
      body = body.replace(regex, value);
    });

    return { subject, body };
  }
};
